import axios from 'axios'
import { APP_ENV, BASE_API_URL, DASHBOARD_DOMAINS, REGEX } from '@/utils/constants'
import LocalStorageService from '@/services/LocalStorageService'
import CookieStorageService from '@/services/CookieStorageService'
import { JobStatusType } from '@/enum'

export const sleep = (milliseconds: number) =>
  new Promise((resolve) => {
    const timeoutID: NodeJS.Timeout = setTimeout(() => {
      resolve(timeoutID)
      clearTimeout(timeoutID)
    }, milliseconds)
  })

async function setDashboardVersion(deployedVersion: any) {
  await sleep(2000)
  localStorage.setItem('version', deployedVersion)
  // eslint-disable-next-line no-restricted-globals
  location.reload()
}

export const checkAppVersion = async () => {
  let result: any
  let deployedVersion: any = null
  const url = `${BASE_API_URL}/versions/dashboard`

  try {
    const response = await axios.get(url)
    if (response != null) {
      result = response
    }
  } catch (error: any) {
    console.error(error)
  }

  if (result?.data) {
    deployedVersion = result.data.value

    const loadedVersion = localStorage.getItem('version')

    if (
      loadedVersion &&
      deployedVersion &&
      loadedVersion.toString() === deployedVersion.toString()
    ) {
      return ''
    }

    await setDashboardVersion(deployedVersion)
  }

  return ''
}

export const memoizedExtractPaths = (() => {
  const cache = new Map<string, string[]>()

  return (url: string): string[] => {
    if (cache.has(url)) return cache.get(url)!

    if (!url.startsWith('/')) url = '/' + url

    const segments = url.split('/').filter(Boolean)

    const paths = segments.map((_, index) => {
      return '/' + segments.slice(0, index + 1).join('/')
    })

    cache.set(url, paths)
    return paths
  }
})()

export const containsValues = (obj: any): boolean => {
  if (!obj) return false
  return Object.values(obj).some((value) => {
    if (typeof value !== 'object') return !!value
    return containsValues(value)
  })
}

export const containsAllValues = (obj: any): boolean => {
  if (!obj) return false
  return Object.values(obj).every((value) => {
    if (typeof value !== 'object') return !!value
    return containsAllValues(value)
  })
}

export const validateAndInitializeObject = <T>(obj: any, Class: new (item: any) => T) => {
  if (containsAllValues(obj)) {
    return new Class(obj)
  }
  return
}

export const iconIsImage = (icon: any) => {
  if (REGEX.IS_IMAGE_URL.test(icon)) return true
  return false
}

export const mapToTitleValueFormat = (
  items: any,
  valueFormatter?: { title: string; value: string }
) => {
  if (valueFormatter?.title && valueFormatter?.value) {
    return (
      items?.map((item: any) => ({
        title: item[valueFormatter.title],
        value: item[valueFormatter.value]
      })) || []
    )
  }

  return items?.map((unit: any) => ({ title: unit.symbol, value: unit.name })) || []
}

export const clearLocalStorageAndCookies = (options?: any) => {
  const localStoragePreserveItems = options?.preserveItemsList

  const localStorageService = new LocalStorageService()
  const cookieStorageService = new CookieStorageService()

  if (localStoragePreserveItems && localStoragePreserveItems?.length > 0) {
    const preservableItems = {} as any

    localStoragePreserveItems.forEach((key: any) => {
      preservableItems[key] = localStorage.getItem(key)
    })

    localStorageService.clearLocalStorage()

    Object.entries(preservableItems).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        localStorage.setItem(key, String(value))
      }
    })
  } else {
    localStorageService.clearLocalStorage()
  }

  cookieStorageService.clearCookies()
}

export const getJobStatusColor = (status: JobStatusType) => {
  switch (+status) {
    case JobStatusType.CANCELLED:
      return 'bg-danger_300 text-danger_700'
    case JobStatusType.OPEN:
      return 'bg-gray_100 text-gray_900 border-gray_900'
    case JobStatusType.INPROGRESS:
      return 'bg-orange_100 text-orange_900 border-orange_900'
    case JobStatusType.COMPLETED:
      return 'bg-emerald_50 text-emerald_600 border-emerald_600'
    default:
      return 'bg-gray_100 text-gray_900 border-gray_900'
  }
}

export const getWebDomain = () => {
  let envDomain = ''
  
  switch (APP_ENV) {
    case 'Development':
      envDomain = DASHBOARD_DOMAINS.DEVELOPMENT.URL
      break
    case 'Staging':
      envDomain = DASHBOARD_DOMAINS.STAGING.URL
      break
    case 'Demo':
      envDomain = DASHBOARD_DOMAINS.DEMO.URL
      break
    case 'Production':
      envDomain = DASHBOARD_DOMAINS.PRODUCTION.URL
      break
    default:
      envDomain = DASHBOARD_DOMAINS.DEVELOPMENT.URL
  }

  return envDomain
}

export const sanitizeFileName = (input: string) => {
  return input.replace(/^[^_]*_/, '')
}